import React from 'react';
import classnames from 'classnames';
import ReactMarkdown from 'react-markdown';
import { ROUTES } from 'shared/constants';
import { stripCitationMarkers, toContentTypeChip } from 'shared/panda/messages/doc-answer';
import type { DocAnswerCard } from 'shared/panda/messages/doc-answer';
import { useSourceName } from 'shared/panda/messages/source-name.hooks';
import { useHubKnowledgeItem } from 'shared/hooks/hub.hooks';
import { useKnowledgeItemsById } from 'shared/hooks/knowledge-items.hooks';
import type { HubSource } from 'shared/panda/agent/ag-ui.constants';
import cardStyles from '../../panda-card.module.scss';
import styles from './answer-card.module.scss';
import { useLang } from 'shared/hooks/lang.hooks';
import { Citations } from '../citations/citations';
import { SourceFooter } from './source-footer';
import { pandaAnalytics } from '../../panda-analytics';

interface AnswerCardProps {
  card: DocAnswerCard;
  /** The chat thread, or the search run's id when rendered outside the chat. */
  conversationId: string | null;
  messageId: string;
  /** 1-based position of this card within the message (analytics). */
  position: number;
  focusable?: boolean;
  /** Hub answer: the source that resolves the item cross-org and picks the org badge. */
  hubSource?: HubSource;
}

/**
 * One per-source answer block: optional context lead-in → answer text → source
 * row → citations. Driven by one entry of a (possibly partial)
 * `show_doc_answer` tool call's `answers[]` (see doc-answer.ts); the title
 * resolves from the knowledge item by `document_id`. The source row renders only
 * once that lookup resolves — while `document_id` is still streaming
 * (partial/unknown) it simply isn't shown.
 */
export function AnswerCard({
  card,
  conversationId,
  messageId,
  position,
  focusable = true,
  hubSource,
}: AnswerCardProps) {
  const lang = useLang('AI_CHAT');
  const isHub = Boolean(hubSource);
  // The row (not the full item) is enough for the title and chip, and it shares
  // the cache with the copy-text hook. A hub answer's item is a cross-org item,
  // resolvable only through the hub copy (the hub-access policy), addressed by
  // the same document_id (the hub twin reuses the source _id).
  const { itemsById } = useKnowledgeItemsById(isHub ? [] : [card.documentId]);
  const ownItem = itemsById[card.documentId];
  const { data: hubItem } = useHubKnowledgeItem(isHub ? card.documentId : undefined);
  const knowledgeItem = isHub ? hubItem : ownItem;

  // The title comes from the resolved knowledge item (not the wire).
  const sourcePrefix = lang.PANDA_SOURCE_PREFIX || 'According to';
  const sourceName = useSourceName(knowledgeItem, isHub);
  const contentTypeChip = knowledgeItem ? toContentTypeChip(knowledgeItem) : null;

  const documentEventProperties = {
    conversationId,
    messageId,
    documentId: card.documentId,
    position,
  };

  return (
    <div className={classnames(cardStyles.card, styles.card)}>
      {card.context && <p className={styles.context}>{card.context}</p>}
      <div className={styles.answerText}>
        <ReactMarkdown>{stripCitationMarkers(card.text)}</ReactMarkdown>
      </div>

      {knowledgeItem && (
        <div className={cardStyles.section}>
          <span className={styles.attribution}>
            {sourcePrefix} <span className={styles.sourceName}>{sourceName}</span>
          </span>
          <SourceFooter
            chip={contentTypeChip}
            isHub={isHub}
            to={(isHub ? ROUTES.KNOWLEDGE_HUB_ITEMS : ROUTES.KNOWLEDGE_ITEMS).replace(
              ':id',
              card.documentId,
            )}
            label={lang.PANDA_OPEN_SOURCE || 'Open'}
            focusable={focusable}
            onClick={() => pandaAnalytics.trackResourceOpen(documentEventProperties)}
          />
        </div>
      )}
      <Citations
        chunkIds={card.chunkIds}
        focusable={focusable}
        hubSource={hubSource}
        onOpen={() => pandaAnalytics.trackOpenReferences(documentEventProperties)}
        onToggleQuote={(citationPosition, expended) =>
          pandaAnalytics.trackToggleReference({
            conversationId,
            messageId,
            documentId: card.documentId,
            position: citationPosition,
            expended,
          })
        }
      />
    </div>
  );
}
